// ============================================================================
// Sub Replacement — request detail. Read-only for AMs after submit. Approvers
// (has_full_am_authority) get inline Approve/Reject actions when status is
// Pending. The full approval queue is on a separate route for batch work;
// this screen handles single-record approval.
// ============================================================================

import { canApproveSubReplacement } from '../../shared/role-gates';
import { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, X } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import {
  Button,
  ErrorState,
  PageHeader,
  Skeleton,
  UrgencyBadge,
} from '../../shared/components';
import type { SubReplacementStatus } from '../../shared/types';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { CopyReference } from '../../shared/copy-reference';
import { useCanManageSubs } from '../../shared/use-can-manage-subs';
import {
  useSubReplacementRequest,
  useSubReplacementRequests,
} from './hooks/use-sub-replacement-data';
import { CutoverModal } from './cutover-modal';
import { fmtNight, nightBefore } from './nights';
import { supabase } from '../../shared/supabase';
import { SubChangeReasonTag, useSubChangeReasons } from './sub-change-reasons';
import { incomingPayText } from './pay-unit';

interface FlashState {
  flash?: string;
}

// Sub replacements carry financial implications (hold-pay, incoming sub rate),
// so finance approves/edits alongside has_full_am_authority. RLS mirrors this
// via srr_update_finance.
function isApprover(role: string | null | undefined): boolean {
  return canApproveSubReplacement(role);
}

const STATUS_CLASSES: Record<SubReplacementStatus, string> = {
  Pending: 'bg-st-warn-bg-strong text-st-warn-fg-strong',
  Approved: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
  Rejected: 'bg-st-danger-bg-strong text-st-danger-fg-strong',
  Cancelled: 'bg-tcs-border text-tcs-text-secondary',
  Matching: 'bg-st-info-bg-strong text-st-info-fg-strong',
  Awaiting_Onboarding: 'bg-indigo-100 text-indigo-900',
  Cutover_Pending: 'bg-violet-100 text-violet-900',
  Completed: 'bg-st-ok-bg-strong text-st-ok-fg-strong',
};

export default function SubReplacementDetail() {
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const flash = (location.state as FlashState | null)?.flash;
  const { profile } = useAuth();
  const { reasons: subChangeReasons } = useSubChangeReasons();

  const { data: row, isLoading, error, refetch } = useSubReplacementRequest(
    requestId ?? null,
  );
  // 2026-06-15 — own-facility AMs can now file, approve, AND cut over a sub
  // change on their own facilities (DB: can_manage_subs + RLS srr_update +
  // cutover RPC). Mirror that authority here so the Approve / Reject / Complete
  // cutover actions render for them, not only the full-authority approver roles.
  const { canManage } = useCanManageSubs(row?.facility_id ?? null);
  const canApprove = isApprover(profile?.role) || canManage;
  // We piggyback on the list hook's updateStatus mutation surface so the
  // mutation logic lives in one place. The mutation reads from the same
  // PostgREST endpoint either way; we just don't need the list rows here.
  const { updateStatus } = useSubReplacementRequests({});

  const [rejecting, setRejecting] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showCutover, setShowCutover] = useState(false);
  // Correcting the handover date on a completed change (TCSD-0336 point 4). One
  // date is edited — the new sub's first night — and the previous sub's last
  // night moves with it to the night before, in one database step.
  const [correcting, setCorrecting] = useState(false);
  const [correctedFirstNight, setCorrectedFirstNight] = useState('');
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [correctionDone, setCorrectionDone] = useState<string | null>(null);
  const [correctionBusy, setCorrectionBusy] = useState(false);

  async function onCorrect() {
    if (!row || !correctedFirstNight) return;
    setCorrectionBusy(true);
    setCorrectionError(null);
    try {
      const { error: e } = await supabase.rpc('sub_replacement_correct_handover', {
        p_request_id: row.request_id,
        p_first_night: correctedFirstNight,
      });
      if (e) throw e;
      setCorrectionDone(
        `Corrected. ${row.incoming_provider?.partner_name ?? 'The new sub'} starts ${fmtNight(correctedFirstNight)}; ${
          row.outgoing_assignment?.provider_name ?? 'the previous sub'
        }'s last night is ${fmtNight(nightBefore(correctedFirstNight))}.`,
      );
      setCorrecting(false);
      setCorrectedFirstNight('');
      refetch();
    } catch (err) {
      setCorrectionError(rpcErrorMessage(err, 'The correction did not save.'));
    } finally {
      setCorrectionBusy(false);
    }
  }

  async function onApprove() {
    if (!row) return;
    setSubmitting(true);
    setActionError(null);
    try {
      await updateStatus(row.request_id, 'Approved');
      refetch();
    } catch (err) {
      // PostgrestError is a plain object (not an Error) — surface the real RLS /
      // trigger message (e.g. "Only roles able to manage subs…") not a generic.
      setActionError(rpcErrorMessage(err, 'Approve failed. Try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  async function onReject() {
    if (!row) return;
    if (!rejectionReason.trim()) {
      setActionError('A rejection reason is required.');
      return;
    }
    setSubmitting(true);
    setActionError(null);
    try {
      await updateStatus(row.request_id, 'Rejected', {
        rejection_reason: rejectionReason.trim(),
      });
      setRejecting(false);
      setRejectionReason('');
      refetch();
    } catch (err) {
      setActionError(rpcErrorMessage(err, 'Reject failed. Try again.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-3xl p-4 pb-28 sm:p-6">
      <PageHeader
        title={
          row ? (
            <span className="flex items-center gap-2">
              <span className="font-mono text-sm text-tcs-text-muted">
                {row.request_id}
              </span>
              {/* AFB-0030 — linkable AP reference for this sub change, so a
                  line on the AP worksheet traces back to the source record. */}
              <CopyReference
                id={row.request_id}
                path={`/sub-replacement/${row.request_id}`}
              />
              <span className="truncate">
                {row.facilities?.facility_name ?? row.facility_id}
              </span>
            </span>
          ) : (
            'Request'
          )
        }
        meta={row ? row.accounts?.account_name ?? row.account_id : null}
        actions={
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft className="h-4 w-4" />}
            onClick={() => navigate('/sub-replacement')}
          >
            Back
          </Button>
        }
      />

      {flash && (
        <div className="mt-4 rounded-md border border-st-ok-border bg-st-ok-bg px-3 py-2 text-sm text-st-ok-fg-strong">
          {flash}
        </div>
      )}

      {isLoading && (
        <div className="mt-6 space-y-3">
          <Skeleton className="h-24 rounded-md" />
          <Skeleton className="h-24 rounded-md" />
        </div>
      )}

      {error && (
        <div className="mt-6">
          <ErrorState title="Could not load request" body={error} onRetry={refetch} />
        </div>
      )}

      {!isLoading && !error && !row && (
        <div className="mt-6">
          <ErrorState
            title="Not found"
            body="No request with that ID, or you don't have access."
          />
        </div>
      )}

      {row && (
        <>
          <section className="mt-6 grid gap-3 rounded-md border border-tcs-border bg-tcs-bg p-4 text-sm sm:grid-cols-2">
            <DetailField label="Status">
              <span
                className={`inline-flex items-center rounded-md px-2 py-[2px] text-[11px] font-semibold uppercase tracking-wide ${
                  STATUS_CLASSES[row.status] ?? 'bg-tcs-surface text-tcs-text-secondary'
                }`}
              >
                {row.status.replace('_', ' ')}
              </span>
            </DetailField>
            <DetailField label="Urgency">
              <UrgencyBadge tier={row.urgency} withTimeline />
            </DetailField>
            <DetailField label="Facility">
              {row.facilities?.facility_name ?? row.facility_id}
              {row.facilities?.market_region
                ? ` · ${row.facilities.market_region}`
                : ''}
            </DetailField>
            <DetailField label="Account">
              {row.accounts?.account_name ?? row.account_id}
            </DetailField>
            {/* AFB-0051 — show the incoming (new) sub by name. Resolved via
                service_providers.partner_name on incoming_provider_id. */}
            <DetailField label="New sub (incoming)">
              {row.incoming_provider?.partner_name
                ?? row.incoming_provider_id
                ?? 'Not yet identified'}
            </DetailField>
            <DetailField label="Previous sub">
              {row.outgoing_assignment_id == null
                ? 'No previous sub (first assignment)'
                : row.outgoing_assignment
                  ? `${row.outgoing_assignment.role_title ?? '—'} · ${
                      row.outgoing_assignment.provider_name
                      ?? row.outgoing_assignment.provider_id
                      ?? 'unassigned'
                    }`
                  : '—'}
            </DetailField>
            <DetailField label="Final payment per agreement (outgoing)">
              {row.hold_pay ? 'On' : 'Off'}
            </DetailField>
            {/* AFB-0027 — pay / incentives / date at a glance for approvers.
                Read-only here; values stay editable until cutover (the
                cutover modal re-asks with these as defaults). */}
            <DetailField label="Incoming contract rate">
              {incomingPayText(row.incoming_pay_rate, row.outgoing_assignment)}
            </DetailField>
            <DetailField label="Scope Performance Payment">
              {row.nmi_enabled
                ? row.nmi_amount != null
                  ? `On · $${row.nmi_amount}/mo`
                  : 'On · amount TBD'
                : row.nmi_enabled === false
                  ? 'Off'
                  : '—'}
            </DetailField>
            <DetailField label="Compliment payment">
              {row.compliment_enabled
                ? row.compliment_amount != null
                  ? `On · $${row.compliment_amount}`
                  : 'On · amount TBD'
                : row.compliment_enabled === false
                  ? 'Off'
                  : '—'}
            </DetailField>
            <DetailField label="Date of change">
              {row.planned_change_date
                ? `${row.planned_change_date}${
                    row.change_date_type === 'historical'
                      ? ' (historical — already happened)'
                      : ' (planned)'
                  }`
                : 'Not set'}
            </DetailField>
            <DetailField label="Requested by">
              {formatEid(row.requester, row.requested_by_eid)}
            </DetailField>
            <DetailField label="Submitted">
              {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
            </DetailField>
            {row.approved_by_eid && (
              <>
                <DetailField label="Decided by">
                  {formatEid(row.approver, row.approved_by_eid)}
                </DetailField>
                <DetailField label="Decided at">
                  {row.approved_date
                    ? new Date(row.approved_date).toLocaleString()
                    : '—'}
                </DetailField>
              </>
            )}
          </section>

          {(row.reason || row.reason_code) && (
            <section className="mt-4 rounded-md border border-tcs-border bg-tcs-bg p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                Why
              </div>
              {row.reason_code && (
                <div className="mt-1">
                  <SubChangeReasonTag code={row.reason_code} reasons={subChangeReasons} />
                </div>
              )}
              {row.reason && (
                <p className="mt-1 whitespace-pre-wrap text-sm text-tcs-text">
                  {row.reason}
                </p>
              )}
            </section>
          )}

          {row.notes && (
            <section className="mt-4 rounded-md border border-tcs-border bg-tcs-bg p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
                Notes
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-tcs-text">
                {row.notes}
              </p>
            </section>
          )}

          {row.rejection_reason && (
            <section className="mt-4 rounded-md border border-rag-red/30 bg-rag-red/5 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-rag-red">
                Rejection reason
              </div>
              <p className="mt-1 whitespace-pre-wrap text-sm text-tcs-text">
                {row.rejection_reason}
              </p>
            </section>
          )}

          {canApprove && row.status === 'Pending' && (
            <section className="mt-6 rounded-md border border-tcs-border bg-tcs-bg p-4">
              <div className="text-sm font-medium text-tcs-text">Decision</div>
              {actionError && (
                <div className="mt-2 rounded-md border border-rag-red/30 bg-rag-red/5 px-3 py-2 text-sm text-rag-red">
                  {actionError}
                </div>
              )}
              {!rejecting ? (
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <Button
                    icon={<Check className="h-4 w-4" />}
                    onClick={onApprove}
                    loading={submitting}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="destructive"
                    icon={<X className="h-4 w-4" />}
                    onClick={() => setRejecting(true)}
                    disabled={submitting}
                  >
                    Reject
                  </Button>
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  <label
                    htmlFor="rejection-reason"
                    className="block text-sm font-medium text-tcs-text"
                  >
                    Rejection reason (required)
                  </label>
                  <textarea
                    id="rejection-reason"
                    className="min-h-[80px] w-full rounded-md border border-tcs-border bg-tcs-bg px-3 py-2 text-base text-tcs-text focus:border-tcs-text focus:outline-none focus:ring-1 focus:ring-tcs-text"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  />
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setRejecting(false);
                        setActionError(null);
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={onReject}
                      loading={submitting}
                    >
                      Submit rejection
                    </Button>
                  </div>
                </div>
              )}
            </section>
          )}
          {canApprove && row.status === 'Approved' && (
            <section className="mt-6 rounded-md border border-tcs-border bg-tcs-bg p-4">
              <div className="text-sm font-medium text-tcs-text">Cutover</div>
              <p className="mt-1 text-xs text-tcs-text-muted">
                Two-stage model: <strong>Approve</strong> authorized the intent
                to swap. <strong>Complete Cut Over</strong> is the second stage
                — the facility&rsquo;s AM confirms the swap actually happened in the field,
                recording the cutover date. It creates the incoming sub&rsquo;s
                assignment (re-confirming the contract rate and performance payments), ends the
                outgoing one, and marks this request Completed.
              </p>
              <div className="mt-3">
                <Button onClick={() => setShowCutover(true)}>Complete cutover</Button>
              </div>
            </section>
          )}

          {canApprove && row.status === 'Completed' && row.outgoing_assignment_id && (
            <section className="mt-6 rounded-md border border-tcs-border bg-tcs-bg p-4">
              <div className="text-sm font-medium text-tcs-text">Correct the handover date</div>
              <p className="mt-1 text-xs text-tcs-text-muted">
                If the change happened on a different night, give the new sub&rsquo;s real first
                night. The previous sub&rsquo;s last night moves with it, so the two can never
                overlap or leave a gap. Who corrected it, and when, is recorded.
              </p>
              {correctionDone && (
                <div className="mt-3 rounded-md border border-st-ok-border bg-st-ok-bg px-3 py-2 text-sm text-st-ok-fg-strong">
                  {correctionDone}
                </div>
              )}
              {!correcting ? (
                <div className="mt-3">
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setCorrecting(true);
                      setCorrectionDone(null);
                      setCorrectionError(null);
                      setCorrectedFirstNight(row.planned_change_date ?? '');
                    }}
                  >
                    Correct the date
                  </Button>
                </div>
              ) : (
                <div className="mt-3 space-y-2">
                  <label htmlFor="corrected-first-night" className="block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
                    First night for {row.incoming_provider?.partner_name ?? 'the new sub'}
                  </label>
                  <input
                    id="corrected-first-night"
                    type="date"
                    value={correctedFirstNight}
                    onChange={(e) => setCorrectedFirstNight(e.target.value)}
                    className="block w-full max-w-xs rounded-md border border-tcs-border bg-tcs-field px-2 py-1.5 text-sm focus:border-tcs-border-input focus:outline-none"
                    data-testid="correct-first-night"
                  />
                  <div
                    className="rounded-md border border-dashed border-tcs-border px-3 py-2 text-[13px] text-tcs-text"
                    data-testid="correct-last-night"
                  >
                    {correctedFirstNight ? (
                      <>
                        {row.outgoing_assignment?.provider_name ?? 'The previous sub'}&rsquo;s last night
                        moves with it to{' '}
                        <strong className="text-st-ok-fg-strong">
                          {fmtNight(nightBefore(correctedFirstNight))}
                        </strong>
                        .
                      </>
                    ) : (
                      'Choose the first night; the last night follows from it.'
                    )}
                  </div>
                  {correctionError && (
                    <div className="rounded-md border border-rag-red/30 bg-rag-red/5 px-3 py-2 text-sm text-rag-red">
                      {correctionError}
                    </div>
                  )}
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setCorrecting(false);
                        setCorrectionError(null);
                        setCorrectedFirstNight('');
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={onCorrect}
                      loading={correctionBusy}
                      disabled={!correctedFirstNight}
                      data-testid="correct-save"
                    >
                      Save the correction
                    </Button>
                  </div>
                </div>
              )}
            </section>
          )}

          {showCutover && (
            <CutoverModal
              requestId={row.request_id}
              facilityId={row.facility_id}
              onClose={() => setShowCutover(false)}
              onDone={() => {
                setShowCutover(false);
                refetch();
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function DetailField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-tcs-text-muted">
        {label}
      </div>
      <div className="mt-0.5 text-sm text-tcs-text">{children}</div>
    </div>
  );
}

function formatEid(
  person: { employee_name_first: string | null; employee_name_last: string | null } | null,
  eid: string | null,
): string {
  if (!person && !eid) return '—';
  if (!person) return eid ?? '—';
  const first = person.employee_name_first ?? '';
  const last = person.employee_name_last ?? '';
  const name = `${first} ${last}`.trim();
  return name || (eid ?? '—');
}
